/**
 * Per-frame preview dispatcher for edit-mode commands (both curve and mesh).
 *
 * Dispatched from commandApplyLive when state.mode === "edit". Handles:
 *   - Curve edit: translate / rotate / scale on curveEditVertices, followed
 *     by surface regeneration.
 *   - Mesh basic transforms: translate / rotate / scale over selected
 *     vertices, with proportional weights and snap overrides.
 *   - Mesh topology ops: parameterized topology commands delegated to
 *     executeTopologyOp via the command metadata registry.
 *   - Mesh specialty ops: shrinkFatten, skinRadius, edgeSlide.
 *
 * Keeps mirror clipping centralized at the tail so every mesh branch lands
 * through the same transient edit-session write path.
 */

import * as THREE from "three/webgpu";
import {
  applyCurveEditVertices,
  applyHandleConstraintsToEditVerts,
  buildCurveEditVertices,
} from "../../modeling/curveOps.ts";
import {
  applyMirrorClipping,
  collectClipPlanes,
} from "../../modeling/mirrorClipping.ts";
import {
  getParamDef,
  getPrimaryParam,
  usesTopologyPreview,
} from "../../modeling/operatorParams.ts";
import {
  computePivot,
  rotatePointFromPivot,
  scalePointFromPivot,
} from "../../modeling/pivot.ts";
import {
  applySnapping,
  querySnap,
  setLastSnapResult,
} from "../../modeling/snap.ts";
import { queryViewportSurface } from "../../modeling/viewportSurfaceRaycast.ts";
import {
  composeSceneObjectWorldMatrix,
  getCollectionForObject,
} from "../collectionGraph.ts";
import {
  regenerateCurveGeometryInMap,
  regenerateCurvePositionsFastInMap,
} from "../curveRegen.ts";
import { patchActiveEditSession } from "../editSession.ts";
import { patchLiveEditPreview } from "../liveEditPreview.ts";
import type { AxisConstraint } from "../storeTypes.ts";
import { commandSnapshotObject } from "../storeUndo.ts";
import { applyLiveEdgeSlide } from "./commandEdgeSlide.ts";
import {
  _scratchMat4,
  _scratchMat4B,
  _scratchVec2A,
  _scratchVec3A,
  _scratchVec3B,
  _scratchVec3C,
  _scratchVec3D,
  type ApplyLiveContext,
  acquireLiveVertsScratch,
  mapParamInput,
} from "./core.ts";
import {
  executeTopologyOp,
  getSnapshotVertexNormals,
  getTopologyPreviewCadenceMs,
} from "./topologyExecute.ts";
import {
  applyEditComponentTransform,
  cursorInObjectLocal,
  type EditTransformComponent,
  visibleHeight,
} from "./transformMath.ts";

/**
 * Dev-only tracking so the selection-identity mismatch warning fires at most
 * once per command. The WeakSet key is the CommandBuffer itself, which is
 * replaced wholesale at command-enter, so the entry is collected when the
 * command ends.
 */
const _selectionIdentityWarned = new WeakSet<object>();

function buildDirtyEditVertexList(
  selectionVertices: readonly number[],
  proportionalWeights: Map<number, number> | null,
): Uint32Array {
  if (!proportionalWeights || proportionalWeights.size === 0) {
    return Uint32Array.from(selectionVertices);
  }

  const dirty = new Uint32Array(
    selectionVertices.length + proportionalWeights.size,
  );
  let w = 0;
  const selected = new Set(selectionVertices);
  for (let i = 0; i < selectionVertices.length; i++) {
    dirty[w++] = selectionVertices[i]!;
  }
  for (const vi of proportionalWeights.keys()) {
    if (selected.has(vi)) continue;
    dirty[w++] = vi;
  }
  return w === dirty.length ? dirty : dirty.slice(0, w);
}

function gatherDirtyVertexPositions(
  vertices: Float32Array,
  dirtyVertices: Uint32Array,
): Float32Array {
  const positions = new Float32Array(dirtyVertices.length * 3);
  for (let i = 0; i < dirtyVertices.length; i++) {
    const vi = dirtyVertices[i]!;
    const src = vi * 3;
    const dst = i * 3;
    positions[dst] = vertices[src]!;
    positions[dst + 1] = vertices[src + 1]!;
    positions[dst + 2] = vertices[src + 2]!;
  }
  return positions;
}

export function applyLiveEditCommand(ctx: ApplyLiveContext): void {
  const { set, state, cb, snap, camera, rect, dx, dy } = ctx;

  const em = state.editMode!;
  const obj = state.sceneObjects.get(em.objectId);
  if (!obj) return;

  // --- Curve branch: transform curveEditVertices, regenerate surface ---
  if (em.isCurve && obj.curveData && em.curveEditVertices) {
    applyLiveCurveEdit(ctx);
    return;
  }

  // --- Mesh branch ---
  const snapObj = commandSnapshotObject(snap, em.objectId);
  if (!snapObj) return;

  // Cache invariant: editPivot, editSelectionVertices,
  // individualOriginsComponents, and individualOriginsProportionalPivots are
  // built once at command-enter from `em.selectedVertices`. They are only
  // valid for that exact Set instance. If selection is swapped underneath
  // us, the drag silently produces wrong geometry (pivot off, falloff center
  // off). Assert in dev so a regression surfaces loudly.
  if (
    import.meta.env.DEV &&
    cb.preEditSelectionIdentity !== null &&
    cb.preEditSelectionIdentity !== em.selectedVertices &&
    !_selectionIdentityWarned.has(cb)
  ) {
    _selectionIdentityWarned.add(cb);
    console.warn(
      `[commandApplyLiveEdit] Selection identity changed mid-command (type=${cb.type}).`,
      " Cached pivot/components will be stale — this indicates a bug in a",
      " selection path that failed to cancel the active command.",
    );
  }
  const srcVerts = snapObj.vertices;
  // Reused across pointermove frames — see acquireLiveVertsScratch. The
  // fresh-allocation pattern (`new Float32Array(srcVerts)`) that used to
  // live here cost ~12 MB GC churn per frame on a 1M-vertex mesh and
  // dominated drag latency.
  const newVerts = acquireLiveVertsScratch(srcVerts.length);
  newVerts.set(srcVerts);

  // Object-to-world matrix. With a parent collection this composes
  // collection.transform ∘ obj local — mirrors what PrimitiveRenderer feeds
  // into its mesh group via `getSceneObjectWorldTransform`. Drag math,
  // pivot projection, and snap all operate in this world frame.
  const objMatrix =
    composeSceneObjectWorldMatrix(
      state.sceneObjects,
      state.collections,
      em.objectId,
      _scratchMat4,
    ) ?? _scratchMat4.identity();

  if (cb.type === "edgeSlide" && cb.edgeSlideRails) {
    // edgeSlide is the one edit-mode command that doesn't consume the
    // pivot cache; it reads the per-vertex rails pre-computed at
    // command-enter. Falls through to the shared mirror-clip + session
    // patch tail.
    applyLiveEdgeSlide(ctx, newVerts, objMatrix);
  } else {
    // Every remaining branch consumes px/py/pz. commandEnter populates
    // editPivot for precisely this set of types (basic transforms plus
    // topology ops) and early-returns when the snapshot lookup fails, so
    // the cache is guaranteed here. A null read would be a structural bug
    // in commandEnter, not a user state.
    const [px, py, pz] = cb.editPivot!;
    // Component pivots and the proportional nearest-pivot lookup are
    // pre-computed at command-enter from the same snapshot — see
    // commandEnter.ts. Building them here on every pointermove was the
    // bottleneck on dense disconnected selections with proportional edit.
    const individualComponents = cb.individualOriginsComponents;

    if (cb.type === "translate") {
      applyLiveMeshTranslate(ctx, srcVerts, newVerts, px, py, pz, objMatrix);
    } else if (cb.type === "scale") {
      applyLiveMeshScale(
        ctx,
        srcVerts,
        newVerts,
        px,
        py,
        pz,
        objMatrix,
        individualComponents,
      );
    } else if (cb.type === "rotate") {
      applyLiveMeshRotate(
        ctx,
        srcVerts,
        newVerts,
        px,
        py,
        pz,
        objMatrix,
        individualComponents,
      );
    } else if (cb.type === "shrinkFatten") {
      applyLiveMeshShrinkFatten(
        ctx,
        snapObj,
        srcVerts,
        newVerts,
        px,
        py,
        pz,
        objMatrix,
      );
    } else if (cb.type === "skinRadius") {
      applyLiveMeshSkinRadius(ctx, snapObj, srcVerts, px, py, pz, objMatrix);
      return; // skinRadii updated — skip vertex-only patchSceneObject below
    } else if (
      usesTopologyPreview(cb.type) &&
      (cb.type !== "edgeLoopInsert" || cb.edgeLoopInsertEdge)
    ) {
      // Topology ops: use parameter registry for input mapping, then
      // delegate to executeTopologyOp for geometry + scene patching.
      const activeKey = cb.activeValueMode ?? getPrimaryParam(cb.type)?.key;
      const paramDef = activeKey ? getParamDef(cb.type, activeKey) : null;
      let nextParams = cb.params;
      if (
        paramDef &&
        (paramDef.type === "float" || paramDef.type === "int") &&
        paramDef.inputMapping
      ) {
        const raw = mapParamInput(
          paramDef,
          dx,
          dy,
          rect,
          camera,
          objMatrix,
          px,
          py,
          pz,
        );
        const scalar = Math.max(
          paramDef.min,
          Math.min(paramDef.max, (paramDef.baseValue ?? 0) + raw),
        );
        const value = paramDef.type === "int" ? Math.round(scalar) : scalar;
        if (cb.params[paramDef.key] !== value) {
          nextParams = { ...cb.params, [paramDef.key]: value };
        }
      }
      const paramsChanged = nextParams !== cb.params;
      if (!paramsChanged && cb.topologyPreviewSeed) {
        return;
      }
      const now = performance.now();
      const previewCadenceMs = getTopologyPreviewCadenceMs(cb.type);
      if (
        previewCadenceMs > 0 &&
        now - cb.topologyPreviewLastAppliedAt < previewCadenceMs
      ) {
        return;
      }
      const topologyResult = executeTopologyOp(
        cb.type,
        nextParams,
        srcVerts,
        snapObj,
        em,
        obj,
        state,
        set,
        {
          preEditFaces: cb.preEditFaces,
          preEditSelection: cb.preEditSelection,
          preEditEdges: cb.preEditEdges,
          preEditActiveVertex: cb.preEditActiveVertex,
          preEditActiveEdge: cb.preEditActiveEdge,
          preEditActiveFace: cb.preEditActiveFace,
          edgeLoopInsertEdge: cb.edgeLoopInsertEdge,
        },
        "preview",
        cb.topologyPreviewSeed,
      );
      if (
        topologyResult.ok &&
        topologyResult.previewSeed &&
        topologyResult.previewSeed !== cb.topologyPreviewSeed
      ) {
        set({
          commandBuffer: {
            ...cb,
            params: nextParams,
            topologyPreviewSeed: topologyResult.previewSeed,
            topologyPreviewLastAppliedAt: now,
          },
        });
      } else if (topologyResult.ok) {
        if (paramsChanged || previewCadenceMs > 0) {
          set({
            commandBuffer: {
              ...cb,
              params: nextParams,
              topologyPreviewLastAppliedAt: now,
            },
          });
        }
      }
      return; // skip shared vertex-only update below
    }
  }

  // Mirror clipping: prevent vertices from crossing mirror planes
  const clipPlanes = collectClipPlanes(
    [],
    state.modifierOverrides.get(em.objectId) ?? [],
  );
  if (clipPlanes.length > 0) {
    // Clamp selected vertices
    applyMirrorClipping(newVerts, clipPlanes, em.selectedVertices, srcVerts);
    // Clamp proportional-weight vertices
    if (cb.proportionalWeights) {
      applyMirrorClipping(
        newVerts,
        clipPlanes,
        cb.proportionalWeights.keys(),
        srcVerts,
      );
    }
  }

  // Apply updated preview vertices without touching committed sceneObjects.
  // Normal recomputation remains deferred until confirm.
  const dirtyVertices = buildDirtyEditVertexList(
    cb.editSelectionVertices ?? Array.from(em.selectedVertices),
    cb.proportionalWeights,
  );
  patchLiveEditPreview(em.objectId, {
    dirtyVertices,
    vertexPatch: {
      indices: dirtyVertices,
      positions: gatherDirtyVertexPositions(newVerts, dirtyVertices),
    },
  });
}

// ===========================================================================
// Curve edit branch
// ===========================================================================

function applyLiveCurveEdit(ctx: ApplyLiveContext): void {
  const {
    set,
    state,
    cb,
    snap,
    camera,
    rect,
    screenX,
    screenY,
    dx,
    dy,
    ox,
    oy,
  } = ctx;
  const em = state.editMode!;
  const obj = state.sceneObjects.get(em.objectId)!;
  const snapObj = commandSnapshotObject(snap, em.objectId);
  if (!snapObj?.curveData) return;
  const srcEditVerts = buildCurveEditVertices(snapObj.curveData);
  const newEditVerts = new Float32Array(srcEditVerts);

  const parentCollection = getCollectionForObject(
    state.collections,
    em.objectId,
  );

  // Pivot from selected edit vertices
  const pivotPositions: [number, number, number][] = [];
  for (const vi of em.selectedVertices) {
    const pi = Math.floor(vi / 3);
    const comp = vi % 3;
    const base = pi * 9 + comp * 3;
    pivotPositions.push([
      srcEditVerts[base],
      srcEditVerts[base + 1],
      srcEditVerts[base + 2],
    ]);
  }
  const [px, py, pz] = computePivot(
    pivotPositions,
    state.pivotReference,
    cursorInObjectLocal(obj, parentCollection, state.cursor3D.position),
  );

  // See applyLiveEditCommand — objMatrix is the world matrix
  // (collection.transform ∘ obj local) so drag projections stay in sync with
  // the rendered overlay position.
  const objMatrix =
    composeSceneObjectWorldMatrix(
      state.sceneObjects,
      state.collections,
      em.objectId,
      _scratchMat4,
    ) ?? _scratchMat4.identity();

  if (cb.type === "translate") {
    const pivotWorld = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix);
    const dist = camera.position.distanceTo(pivotWorld);
    const vh = visibleHeight(camera, dist);
    const worldPerPx = vh / rect.height;
    const invObj = _scratchMat4B.copy(objMatrix).invert();
    const camRight = _scratchVec3A;
    const camUp = _scratchVec3B;
    camera.matrixWorld.extractBasis(camRight, camUp, _scratchVec3D);
    const localRight = camRight.transformDirection(invObj);
    const localUp = camUp.transformDirection(invObj);

    let moveDir: THREE.Vector3;
    if (cb.axis === "normal" && cb.normalVector) {
      // normalVector is in object-local space (resolved by resolveKeyboardAxis for edit mode)
      const [nvx, nvy, nvz] = cb.normalVector;
      const axisSS = _scratchVec3C
        .set(px + nvx, py + nvy, pz + nvz)
        .applyMatrix4(objMatrix)
        .project(camera);
      const originSS = _scratchVec3D
        .set(px, py, pz)
        .applyMatrix4(objMatrix)
        .project(camera);
      const axisDirSS = _scratchVec2A.set(
        axisSS.x - originSS.x,
        axisSS.y - originSS.y,
      );
      if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
      const ndcDx = (dx / rect.width) * 2;
      const ndcDy = -(dy / rect.height) * 2;
      const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
      const worldDist = proj * vh;
      moveDir = _scratchVec3C
        .set(nvx, nvy, nvz)
        .multiplyScalar(worldDist)
        .clone();
    } else if (cb.axis && cb.axis !== "normal") {
      const axisVec = _scratchVec3C.set(
        cb.axis === "x" ? 1 : 0,
        cb.axis === "y" ? 1 : 0,
        cb.axis === "z" ? 1 : 0,
      );
      const axisSS = _scratchVec3D
        .copy(axisVec)
        .applyMatrix4(objMatrix)
        .project(camera);
      const originSS = _scratchVec3A
        .set(px, py, pz)
        .applyMatrix4(objMatrix)
        .project(camera);
      const axisDirSS = _scratchVec2A.set(
        axisSS.x - originSS.x,
        axisSS.y - originSS.y,
      );
      if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
      const ndcDx = (dx / rect.width) * 2;
      const ndcDy = -(dy / rect.height) * 2;
      const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
      const worldDist = proj * vh;
      moveDir = axisVec.multiplyScalar(worldDist);
    } else {
      moveDir = localRight
        .multiplyScalar(dx * worldPerPx)
        .add(localUp.multiplyScalar(-dy * worldPerPx));
    }
    for (const vi of em.selectedVertices) {
      const pi = Math.floor(vi / 3);
      const comp = vi % 3;
      const base = pi * 9 + comp * 3;
      newEditVerts[base] = srcEditVerts[base] + moveDir.x;
      newEditVerts[base + 1] = srcEditVerts[base + 1] + moveDir.y;
      newEditVerts[base + 2] = srcEditVerts[base + 2] + moveDir.z;
      // Drag handles along with their control point
      if (comp === 0) {
        for (const hComp of [1, 2]) {
          if (em.selectedVertices.has(vi + hComp)) continue;
          const hBase = pi * 9 + hComp * 3;
          newEditVerts[hBase] = srcEditVerts[hBase] + moveDir.x;
          newEditVerts[hBase + 1] = srcEditVerts[hBase + 1] + moveDir.y;
          newEditVerts[hBase + 2] = srcEditVerts[hBase + 2] + moveDir.z;
        }
      }
    }
  } else if (cb.type === "scale") {
    const pivotWorld = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix);
    const pivotSS = pivotWorld.project(camera);
    const pivotPx = _scratchVec2A.set(
      (pivotSS.x * 0.5 + 0.5) * rect.width,
      (-pivotSS.y * 0.5 + 0.5) * rect.height,
    );
    const initDist = Math.max(1, Math.hypot(ox - pivotPx.x, oy - pivotPx.y));
    const currDist = Math.max(
      1,
      Math.hypot(screenX - pivotPx.x, screenY - pivotPx.y),
    );
    const factor = currDist / initDist;
    if (cb.axis === "normal" && cb.normalVector) {
      const [nvx, nvy, nvz] = cb.normalVector;
      const len2 = nvx * nvx + nvy * nvy + nvz * nvz;
      const s = factor - 1;
      const applyOrientedScale = (b: number) => {
        const ox = srcEditVerts[b] - px,
          oy = srcEditVerts[b + 1] - py,
          oz = srcEditVerts[b + 2] - pz;
        const dot = len2 > 1e-12 ? (ox * nvx + oy * nvy + oz * nvz) / len2 : 0;
        newEditVerts[b] = px + ox + s * dot * nvx;
        newEditVerts[b + 1] = py + oy + s * dot * nvy;
        newEditVerts[b + 2] = pz + oz + s * dot * nvz;
      };
      for (const vi of em.selectedVertices) {
        const pi = Math.floor(vi / 3);
        const comp = vi % 3;
        applyOrientedScale(pi * 9 + comp * 3);
        if (comp === 0) {
          for (const hComp of [1, 2]) {
            if (em.selectedVertices.has(vi + hComp)) continue;
            applyOrientedScale(pi * 9 + hComp * 3);
          }
        }
      }
    } else {
      const axisConstrained = cb.axis && cb.axis !== "normal";
      const fx = cb.axis === "x" ? factor : axisConstrained ? 1 : factor;
      const fy = cb.axis === "y" ? factor : axisConstrained ? 1 : factor;
      const fz = cb.axis === "z" ? factor : axisConstrained ? 1 : factor;
      for (const vi of em.selectedVertices) {
        const pi = Math.floor(vi / 3);
        const comp = vi % 3;
        const base = pi * 9 + comp * 3;
        newEditVerts[base] = px + (srcEditVerts[base] - px) * fx;
        newEditVerts[base + 1] = py + (srcEditVerts[base + 1] - py) * fy;
        newEditVerts[base + 2] = pz + (srcEditVerts[base + 2] - pz) * fz;
        // Scale handles along with their control point
        if (comp === 0) {
          for (const hComp of [1, 2]) {
            if (em.selectedVertices.has(vi + hComp)) continue;
            const hBase = pi * 9 + hComp * 3;
            newEditVerts[hBase] = px + (srcEditVerts[hBase] - px) * fx;
            newEditVerts[hBase + 1] = py + (srcEditVerts[hBase + 1] - py) * fy;
            newEditVerts[hBase + 2] = pz + (srcEditVerts[hBase + 2] - pz) * fz;
          }
        }
      }
    }
  } else if (cb.type === "rotate") {
    const pivotWorld = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix);
    const pivotSS = pivotWorld.project(camera);
    const pivotPx = _scratchVec2A.set(
      (pivotSS.x * 0.5 + 0.5) * rect.width,
      (-pivotSS.y * 0.5 + 0.5) * rect.height,
    );
    const startAngle = Math.atan2(oy - pivotPx.y, ox - pivotPx.x);
    const currAngle = Math.atan2(screenY - pivotPx.y, screenX - pivotPx.x);
    const deltaAngle = currAngle - startAngle;
    let axis: THREE.Vector3;
    if (cb.axis === "normal" && cb.normalVector) {
      axis = _scratchVec3C
        .set(cb.normalVector[0], cb.normalVector[1], cb.normalVector[2])
        .normalize();
    } else if (cb.axis && cb.axis !== "normal") {
      axis = _scratchVec3C.set(
        cb.axis === "x" ? 1 : 0,
        cb.axis === "y" ? 1 : 0,
        cb.axis === "z" ? 1 : 0,
      );
    } else {
      camera.getWorldDirection(_scratchVec3C);
      const invObj = _scratchMat4B.copy(objMatrix).invert();
      axis = _scratchVec3C.transformDirection(invObj).normalize();
    }
    const rotMat = _scratchMat4B.makeRotationAxis(axis, deltaAngle);
    const v = _scratchVec3D;
    for (const vi of em.selectedVertices) {
      const pi = Math.floor(vi / 3);
      const comp = vi % 3;
      const base = pi * 9 + comp * 3;
      v.set(
        srcEditVerts[base] - px,
        srcEditVerts[base + 1] - py,
        srcEditVerts[base + 2] - pz,
      );
      v.applyMatrix4(rotMat);
      newEditVerts[base] = v.x + px;
      newEditVerts[base + 1] = v.y + py;
      newEditVerts[base + 2] = v.z + pz;
      // Rotate handles along with their control point
      if (comp === 0) {
        for (const hComp of [1, 2]) {
          if (em.selectedVertices.has(vi + hComp)) continue;
          const hBase = pi * 9 + hComp * 3;
          v.set(
            srcEditVerts[hBase] - px,
            srcEditVerts[hBase + 1] - py,
            srcEditVerts[hBase + 2] - pz,
          );
          v.applyMatrix4(rotMat);
          newEditVerts[hBase] = v.x + px;
          newEditVerts[hBase + 1] = v.y + py;
          newEditVerts[hBase + 2] = v.z + pz;
        }
      }
    }
  } else {
    return; // unsupported command type for curves
  }

  // Enforce handle constraints
  applyHandleConstraintsToEditVerts(
    newEditVerts,
    snapObj.curveData,
    em.selectedVertices,
  );

  // Write back to curveData and update surface positions only. Full regeneration
  // (topology, loopNormals, UVs) is deferred to confirmCommand — see the curve
  // branch there. Live drag pays only the per-sample spline eval + ring stamp.
  // Stable `topology` identity across frames also keeps EditModeRenderer's
  // `nearestDist` useMemo from re-firing per frame.
  const updatedCurveData = applyCurveEditVertices(
    snapObj.curveData,
    newEditVerts,
  );
  const fastObjects = regenerateCurvePositionsFastInMap(
    state.sceneObjects,
    em.objectId,
    updatedCurveData,
  );
  // Fallback: vertex count mismatch (shouldn't happen mid-drag) — full regen
  // rebuilds topology / normals / UVs for the whole cascade.
  const objects =
    fastObjects ??
    regenerateCurveGeometryInMap(
      state.sceneObjects,
      em.objectId,
      updatedCurveData,
    );
  set({
    sceneObjects: objects,
    editMode: { ...em, curveEditVertices: newEditVerts },
  });
}

// Placeholder — mesh basic + specialty branches appended via Edit.
function applyLiveMeshTranslate(
  ctx: ApplyLiveContext,
  srcVerts: Float32Array,
  newVerts: Float32Array,
  px: number,
  py: number,
  pz: number,
  objMatrix: THREE.Matrix4,
): void {
  const {
    state,
    cb,
    camera,
    rect,
    screenX,
    screenY,
    dx,
    dy,
    snapGridLevelOffset,
  } = ctx;
  const em = state.editMode!;

  // Project mouse delta into world space along the camera plane
  const pivotW = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix).clone();
  const dist = camera.position.distanceTo(pivotW);
  const vh = visibleHeight(camera, dist);
  const worldPerPx = vh / rect.height;

  const camRight = _scratchVec3A;
  const camUp = _scratchVec3B;
  camera.matrixWorld.extractBasis(camRight, camUp, _scratchVec3D);

  // Work in object-local space — transform camera axes into local
  const invO = _scratchMat4B.copy(objMatrix).invert();
  const localRight = camRight.transformDirection(invO);
  const localUp = camUp.transformDirection(invO);

  let moveDir: THREE.Vector3;
  if (cb.axis === "normal" && cb.normalVector) {
    // Project normal vector at pivot into screen space
    const [nvx, nvy, nvz] = cb.normalVector;
    const axisSS = _scratchVec3C
      .set(px + nvx, py + nvy, pz + nvz)
      .applyMatrix4(objMatrix)
      .project(camera);
    const originSS = _scratchVec3D
      .set(px, py, pz)
      .applyMatrix4(objMatrix)
      .project(camera);
    const axisDirSS = _scratchVec2A.set(
      axisSS.x - originSS.x,
      axisSS.y - originSS.y,
    );
    if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
    const ndcDx = (dx / rect.width) * 2;
    const ndcDy = -(dy / rect.height) * 2;
    const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
    const worldDist = proj * vh;
    // Detach from `_scratchVec3C` — the snap-projection block below
    // reuses the same scratch slot for `pivotLocal`, which would
    // silently zero out an aliased moveDir when it gets overwritten.
    // The free-drag branch is already safe because it builds moveDir
    // out of `_scratchVec3A`/`_scratchVec3B`, but the two axis
    // branches both pass through `_scratchVec3C` and MUST clone here.
    moveDir = _scratchVec3C
      .set(nvx, nvy, nvz)
      .multiplyScalar(worldDist)
      .clone();
  } else if (cb.axis && cb.axis !== "normal") {
    const axisVec = _scratchVec3C.set(
      cb.axis === "x" ? 1 : 0,
      cb.axis === "y" ? 1 : 0,
      cb.axis === "z" ? 1 : 0,
    );
    // Project mouse delta onto this axis (screen space)
    const axisSS = _scratchVec3D
      .copy(axisVec)
      .applyMatrix4(objMatrix)
      .project(camera);
    const originSS = _scratchVec3A
      .set(px, py, pz)
      .applyMatrix4(objMatrix)
      .project(camera);
    const axisDirSS = _scratchVec2A.set(
      axisSS.x - originSS.x,
      axisSS.y - originSS.y,
    );
    if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
    const ndcDx = (dx / rect.width) * 2;
    const ndcDy = -(dy / rect.height) * 2;
    const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
    const worldDist = proj * vh;
    // See the .clone() comment in the normal-vector branch above —
    // axisVec is `_scratchVec3C`, so multiplyScalar would alias
    // moveDir to a slot that gets overwritten by `pivotLocal` below.
    moveDir = axisVec.multiplyScalar(worldDist).clone();
  } else {
    moveDir = localRight
      .multiplyScalar(dx * worldPerPx)
      .add(localUp.multiplyScalar(-dy * worldPerPx));
  }

  // Snap override: convert to world space for applySnapping, then back to local
  const pivotLocal = _scratchVec3C.set(px, py, pz);
  const moveDirWorld = pivotLocal
    .clone()
    .add(moveDir)
    .applyMatrix4(objMatrix)
    .sub(pivotW);

  let snapAxis: AxisConstraint = cb.axis;
  let snapNormal: [number, number, number] | null = null;
  if (cb.axis === "normal" && cb.normalVector) {
    const nvW = _scratchVec3C
      .set(...cb.normalVector)
      .transformDirection(objMatrix)
      .normalize();
    snapNormal = [nvW.x, nvW.y, nvW.z];
  } else if (cb.axis && cb.axis !== "normal") {
    // Edit mode uses local axes — map to world normal for snapping
    const av = _scratchVec3C.set(
      cb.axis === "x" ? 1 : 0,
      cb.axis === "y" ? 1 : 0,
      cb.axis === "z" ? 1 : 0,
    );
    const avW = av.transformDirection(objMatrix).normalize();
    snapAxis = "normal";
    snapNormal = [avW.x, avW.y, avW.z];
  }

  applySnapping(
    state,
    screenX,
    screenY,
    rect,
    pivotW,
    moveDirWorld,
    snapAxis,
    snapNormal,
    new Set([em.objectId]),
    snapGridLevelOffset,
  );

  // Convert moveDirWorld back to local displacement
  moveDir
    .copy(pivotW)
    .add(moveDirWorld)
    .applyMatrix4(invO)
    .sub(_scratchVec3C.set(px, py, pz));

  for (const vi of em.selectedVertices) {
    newVerts[vi * 3] = srcVerts[vi * 3] + moveDir.x;
    newVerts[vi * 3 + 1] = srcVerts[vi * 3 + 1] + moveDir.y;
    newVerts[vi * 3 + 2] = srcVerts[vi * 3 + 2] + moveDir.z;
  }
  if (cb.proportionalWeights) {
    for (const [vi, w] of cb.proportionalWeights) {
      newVerts[vi * 3] = srcVerts[vi * 3] + moveDir.x * w;
      newVerts[vi * 3 + 1] = srcVerts[vi * 3 + 1] + moveDir.y * w;
      newVerts[vi * 3 + 2] = srcVerts[vi * 3 + 2] + moveDir.z * w;
    }
  }

  if (
    state.snapEnabled &&
    state.snapTarget === "surface" &&
    state.surfaceProjectIndividual
  ) {
    const influenceWeights = new Map<number, number>();
    for (const vi of em.selectedVertices) influenceWeights.set(vi, 1);
    if (cb.proportionalWeights) {
      for (const [vi, weight] of cb.proportionalWeights) {
        influenceWeights.set(
          vi,
          Math.max(influenceWeights.get(vi) ?? 0, weight),
        );
      }
    }

    const invObjSurface = _scratchMat4B.copy(objMatrix).invert();
    const excludeSurfaceIds = new Set<string>([em.objectId]);
    camera.updateMatrixWorld();
    for (const [vi, weight] of influenceWeights) {
      const base = vi * 3;
      const movedWorldNdc = _scratchVec3A
        .set(newVerts[base], newVerts[base + 1], newVerts[base + 2])
        .applyMatrix4(objMatrix)
        .project(camera);
      if (movedWorldNdc.z < -1 || movedWorldNdc.z > 1) continue;

      const hit = queryViewportSurface(
        movedWorldNdc.x,
        movedWorldNdc.y,
        excludeSurfaceIds,
      );
      if (!hit) continue;

      const projectedLocal = _scratchVec3B
        .set(hit.worldPosition[0], hit.worldPosition[1], hit.worldPosition[2])
        .applyMatrix4(invObjSurface);
      newVerts[base] += (projectedLocal.x - newVerts[base]) * weight;
      newVerts[base + 1] += (projectedLocal.y - newVerts[base + 1]) * weight;
      newVerts[base + 2] += (projectedLocal.z - newVerts[base + 2]) * weight;
    }
  }
}

function applyLiveMeshScale(
  ctx: ApplyLiveContext,
  srcVerts: Float32Array,
  newVerts: Float32Array,
  px: number,
  py: number,
  pz: number,
  objMatrix: THREE.Matrix4,
  individualComponents: EditTransformComponent[] | null,
): void {
  const { state, cb, camera, rect, screenX, screenY, ox, oy } = ctx;
  const em = state.editMode!;

  // Scale: ratio of distance from mouse to pivot (screen space)
  const pivotWorld = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix);
  const pivotSS = pivotWorld.project(camera);
  const pivotPx = _scratchVec2A.set(
    (pivotSS.x * 0.5 + 0.5) * rect.width,
    (-pivotSS.y * 0.5 + 0.5) * rect.height,
  );
  const initDist = Math.max(1, Math.hypot(ox - pivotPx.x, oy - pivotPx.y));
  const currDist = Math.max(
    1,
    Math.hypot(screenX - pivotPx.x, screenY - pivotPx.y),
  );
  const factor = currDist / initDist;

  // Pre-compute normalVector direction for oriented scale
  const hasNV = cb.axis === "normal" && cb.normalVector;
  const nvx = hasNV ? cb.normalVector![0] : 0;
  const nvy = hasNV ? cb.normalVector![1] : 0;
  const nvz = hasNV ? cb.normalVector![2] : 0;
  const pivotPoint: [number, number, number] = [px, py, pz];
  const selectionVertices =
    cb.editSelectionVertices ?? Array.from(em.selectedVertices);
  applyEditComponentTransform(
    srcVerts,
    newVerts,
    individualComponents ?? [
      { pivot: pivotPoint, vertices: selectionVertices },
    ],
    cb.proportionalWeights,
    (point, pivot, weight) => {
      const blended = 1 + (factor - 1) * weight;
      return scalePointFromPivot(
        point,
        pivot,
        blended,
        cb.axis && cb.axis !== "normal" ? cb.axis : null,
        hasNV ? [nvx, nvy, nvz] : null,
      );
    },
    cb.individualOriginsProportionalPivots,
  );
}

function applyLiveMeshRotate(
  ctx: ApplyLiveContext,
  srcVerts: Float32Array,
  newVerts: Float32Array,
  px: number,
  py: number,
  pz: number,
  objMatrix: THREE.Matrix4,
  individualComponents: EditTransformComponent[] | null,
): void {
  const { state, cb, camera, rect, screenX, screenY, ox, oy } = ctx;
  const em = state.editMode!;

  // Rotate: angle from pivot in screen space
  const pivotWorld = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix);
  const pivotSS = pivotWorld.project(camera);
  const pivotPx = _scratchVec2A.set(
    (pivotSS.x * 0.5 + 0.5) * rect.width,
    (-pivotSS.y * 0.5 + 0.5) * rect.height,
  );
  const startAngle = Math.atan2(oy - pivotPx.y, ox - pivotPx.x);
  const currAngle = Math.atan2(screenY - pivotPx.y, screenX - pivotPx.x);
  const deltaAngle = currAngle - startAngle;

  // Determine rotation axis (in object-local space)
  let axis: THREE.Vector3;
  if (cb.axis === "normal" && cb.normalVector) {
    // Oriented axis constraint — normalVector is already in object-local space
    axis = _scratchVec3C
      .set(cb.normalVector[0], cb.normalVector[1], cb.normalVector[2])
      .normalize();
  } else if (cb.axis && cb.axis !== "normal") {
    axis = _scratchVec3C.set(
      cb.axis === "x" ? 1 : 0,
      cb.axis === "y" ? 1 : 0,
      cb.axis === "z" ? 1 : 0,
    );
  } else {
    // Default: rotate around the camera's view direction (in object-local space)
    camera.getWorldDirection(_scratchVec3C);
    const invObj = _scratchMat4B.copy(objMatrix).invert();
    axis = _scratchVec3C.transformDirection(invObj).normalize();
  }
  const axisTuple: [number, number, number] = [axis.x, axis.y, axis.z];
  const pivotPoint: [number, number, number] = [px, py, pz];
  const selectionVertices =
    cb.editSelectionVertices ?? Array.from(em.selectedVertices);
  applyEditComponentTransform(
    srcVerts,
    newVerts,
    individualComponents ?? [
      { pivot: pivotPoint, vertices: selectionVertices },
    ],
    cb.proportionalWeights,
    (point, pivot, weight) =>
      rotatePointFromPivot(point, pivot, axisTuple, deltaAngle * weight),
    cb.individualOriginsProportionalPivots,
  );
}

function applyLiveMeshShrinkFatten(
  ctx: ApplyLiveContext,
  snapObj: any,
  srcVerts: Float32Array,
  newVerts: Float32Array,
  px: number,
  py: number,
  pz: number,
  objMatrix: THREE.Matrix4,
): void {
  const { set, state, cb, camera, rect, screenX, screenY, dx } = ctx;
  const em = state.editMode!;

  // Shrink/Fatten: move each selected vertex along its smoothed
  // per-vertex normal (arithmetic mean of incident loop normals).
  //
  // Earlier revisions indexed `snapObj.loopNormals[vi * 3]` directly,
  // but loopNormals is per-corner — not per-vertex — so that was
  // reading whichever unrelated corner happened to sit at that
  // offset. On any mesh with sharp edges or shared vertices it
  // produced visibly wrong directions, making "pillow"-style
  // inflation on a subdivided cube bulge along random axes instead
  // of outward. `loopToVertexNormals` averages the incident corners.
  //
  // dx maps to world-space offset scaled by camera distance.
  const pivotWorld = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix);
  const dist = camera.position.distanceTo(pivotWorld);
  const vh = visibleHeight(camera, dist);
  const worldPerPx = vh / rect.height;
  let amount = dx * worldPerPx;
  const vertexNormals = getSnapshotVertexNormals(snapObj);

  // Snap override: project snap target onto the reference vertex's
  // normal to derive the offset amount. Picks the selected vertex
  // closest to the cursor as the reference.
  if (state.snapEnabled) {
    const ndcX = ((screenX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((screenY - rect.top) / rect.height) * 2 + 1;
    const snapResult = querySnap(
      ndcX,
      ndcY,
      state.snapTarget,
      new Set<string>(),
    );
    setLastSnapResult(snapResult);
    if (snapResult) {
      const invObj = _scratchMat4B.copy(objMatrix).invert();
      const snapLocalTarget = new THREE.Vector3(
        ...snapResult.worldPosition,
      ).applyMatrix4(invObj);

      let refVi = -1;
      let refDistSq = Infinity;
      for (const vi of em.selectedVertices) {
        const vSS = _scratchVec3D
          .set(srcVerts[vi * 3], srcVerts[vi * 3 + 1], srcVerts[vi * 3 + 2])
          .applyMatrix4(objMatrix)
          .project(camera);
        const d2 = (vSS.x - ndcX) ** 2 + (vSS.y - ndcY) ** 2;
        if (d2 < refDistSq) {
          refDistSq = d2;
          refVi = vi;
        }
      }
      if (refVi >= 0) {
        const rnx = vertexNormals[refVi * 3];
        const rny = vertexNormals[refVi * 3 + 1];
        const rnz = vertexNormals[refVi * 3 + 2];
        const nLen2 = rnx * rnx + rny * rny + rnz * rnz;
        if (nLen2 > 1e-12) {
          const vx = srcVerts[refVi * 3],
            vy = srcVerts[refVi * 3 + 1],
            vz = srcVerts[refVi * 3 + 2];
          amount =
            ((snapLocalTarget.x - vx) * rnx +
              (snapLocalTarget.y - vy) * rny +
              (snapLocalTarget.z - vz) * rnz) /
            nLen2;
        }
      }
    }
  } else {
    setLastSnapResult(null);
  }

  // Store offset in params for redo panel / status display
  set({ commandBuffer: { ...cb, params: { ...cb.params, offset: amount } } });

  // Displace a single vertex along its smoothed normal, scaled by
  // the effective amount (full for selected, falloff-weighted for
  // proportional neighbors). Inlined as a closure so both loops
  // below stay tight and cache-friendly.
  const displaceAlongNormal = (vi: number, scaled: number) => {
    const nx = vertexNormals[vi * 3];
    const ny = vertexNormals[vi * 3 + 1];
    const nz = vertexNormals[vi * 3 + 2];
    const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (nLen < 1e-10) return;
    const k = scaled / nLen;
    newVerts[vi * 3] = srcVerts[vi * 3] + nx * k;
    newVerts[vi * 3 + 1] = srcVerts[vi * 3 + 1] + ny * k;
    newVerts[vi * 3 + 2] = srcVerts[vi * 3 + 2] + nz * k;
  };

  for (const vi of em.selectedVertices) displaceAlongNormal(vi, amount);
  if (cb.proportionalWeights) {
    for (const [vi, w] of cb.proportionalWeights) {
      if (em.selectedVertices.has(vi)) continue;
      displaceAlongNormal(vi, amount * w);
    }
  }
}

function applyLiveMeshSkinRadius(
  ctx: ApplyLiveContext,
  snapObj: any,
  srcVerts: Float32Array,
  px: number,
  py: number,
  pz: number,
  objMatrix: THREE.Matrix4,
): void {
  const { set, state, cb, camera, rect, dx } = ctx;
  const em = state.editMode!;

  // Skin Radius: scale per-vertex skin radius for selected vertices.
  // Mouse dx maps to world-space delta (same mapping as shrinkFatten).
  // Radii are modified in-place from snapshot values — no vertex positions change.
  const pivotWorld = _scratchVec3C.set(px, py, pz).applyMatrix4(objMatrix);
  const dist = camera.position.distanceTo(pivotWorld);
  const vh = visibleHeight(camera, dist);
  const worldPerPx = vh / rect.height;
  const factor = dx * worldPerPx;

  // Store factor in params for StatusBar display
  set({ commandBuffer: { ...cb, params: { ...cb.params, factor } } });

  // Restore radii from snapshot and apply delta to selected verts
  const srcRadii = snapObj.skinRadii;
  const newRadii = srcRadii
    ? new Float32Array(srcRadii)
    : new Float32Array(srcVerts.length / 3).fill(1);
  for (const vi of em.selectedVertices) {
    newRadii[vi] = Math.max(0, (srcRadii ? srcRadii[vi] : 1) + factor);
  }

  set({
    activeEditSession: patchActiveEditSession(
      state.activeEditSession,
      em.objectId,
      { skinRadii: newRadii },
    ),
  });
}
